import React from 'react';
import { Home, Bath, Square, MapPin, Phone, Mail, Globe } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

// Componente para imágenes de propiedades
const PropertyImage = ({ url, alt, className }) => {
  if (!url) return (
    <img
      src="/default-property-image.jpg"
      alt={alt}
      className={className}
    />
  );

  const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';
  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        console.error('Error loading property image:', fullUrl);
        e.target.src = '/default-property-image.jpg';
      }}
    />
  );
};

const LuxuryHomeFlyer = ({ property }) => {
  // Datos de la propiedad con fallbacks
  const propertyData = property || {
    titulo: 'BEST LUXURY HOME FOR SALE',
    tipo: 'venta',
    operacion: 'venta',
    precio_soles: 1500000,
    precio_dolares: 405000,
    ubicacion: '4593 Highland Meadow Park Hanover, NY 21076',
    habitaciones: 3,
    banos: 3,
    area_total: 150,
    telefono: '+312-692-0767',
    email: 'info@companyname.com',
    descripcion_corta: 'Discover luxury living in this stunning modern home. With sleek lines, open concept design and high-end finishes, this property is truly one of a kind.',
    imagenes: [],
    vendedor: {
      nombre: 'Tower Luxury Real Estate',
      foto_perfil: '/agent-profile.jpg'
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US').format(price);
  };

  // Generate property URL
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = property?.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${property?.id || 'casa-123'}`;
  };

  // Características de la propiedad
  const features = [
    { label: 'Dormitorios', value: propertyData.habitaciones },
    { label: 'Baños', value: propertyData.banos },
    { label: 'Seguridad', value: '' },
    { label: 'Servicios', value: '' },
    { label: 'Área Total', value: propertyData.area_total + 'm²' },
  ];

  return (
    <div className="flex flex-col bg-gray-800 text-white w-full max-w-4xl mx-auto">
      {/* Header Section */}
      <div className="flex bg-gray-800 p-4">
        <div className="w-1/4">
          <div className="bg-white p-4 w-24 h-24 flex items-center justify-center">
            <QRCodeSVG 
              value={getPropertyUrl()} 
              size={80} 
              bgColor="#ffffff"
              fgColor="#000000"
              level="L"
            />
          </div>
        </div>
        <div className="w-3/4 flex items-center">
          <h1 className="text-4xl font-bold">
            {propertyData.titulo}
          </h1>
        </div>
      </div>

      {/* Orange Divider */}
      <div className="h-2 bg-orange-500 w-full"></div>

      {/* Main Content Section */}
      <div className="flex bg-teal-800">
        {/* Left Column - Main House Image */}
        <div className="w-1/2 relative">
          <PropertyImage 
            url={propertyData.imagenes[0]}
            alt="Modern Luxury Home Exterior" 
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 left-0 bg-gray-800 flex items-center p-4">
            <div className="bg-orange-500 p-2 mr-2">
              <Home color="white" size={24} />
            </div>
            <span className="text-xl font-bold">
              {propertyData.operacion === 'alquiler' ? 'EN ALQUILER' : 'EN VENTA'}
            </span>
          </div>
        </div>

        {/* Right Column - Interior Images */}
        <div className="w-1/2 p-4 space-y-4">
          <PropertyImage 
            url={propertyData.imagenes[1]}
            alt="Luxury Bedroom" 
            className="w-full h-40 object-cover"
          />
          <PropertyImage 
            url={propertyData.imagenes[2]}
            alt="Luxury Living Room" 
            className="w-full h-40 object-cover"
          />
        </div>
      </div>

      {/* Info Section */}
      <div className="flex">
        {/* About Section */}
        <div className="w-1/2 bg-white text-gray-800 p-8">
          <h2 className="text-4xl font-bold mb-4">Descripción:</h2>
          <p className="text-lg">
            {propertyData.descripcion_corta}
          </p>
          <div className="bg-orange-500 text-white p-4 mt-8 w-full">
            <p className="text-xl font-medium">Contáctanos</p>
          </div>
          
          {/* Contact Info */}
          <div className="mt-8 space-y-4">
            <div className="flex items-center">
              <Phone className="text-orange-500 mr-2" size={24} />
              <span className="text-lg">{propertyData.vendedor?.telefono || 'Consultar'}</span>
            </div>
            <div className="flex items-center">
              <Mail className="text-orange-500 mr-2" size={24} />
              <span className="text-lg">{propertyData.vendedor?.email || 'Consultar'}</span>
            </div>
            <div className="flex items-center">
              <MapPin className="text-orange-500 mr-2" size={24} />
              <span className="text-lg">{propertyData.ubicacion}</span>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="w-1/2 flex flex-col">
          <div className="bg-orange-500 text-white p-8 flex-1">
            <h2 className="text-4xl font-bold mb-6">Caracteristicas</h2>
            <ul className="space-y-2 text-xl">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{feature.label === 'Dormitorios' ? `${feature.value} Dormitorios` : 
                          feature.label === 'Baños' ? `${feature.value} Baños` : 
                          feature.value ? `${feature.value} ${feature.label}` : feature.label}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Price Section */}
          <div className="bg-teal-800 text-white p-8">
            <p className="text-center">Precio</p>
            <p className="text-5xl font-bold text-center mt-2">
              {propertyData.precio_dolares > 0 
                ? `$${formatPrice(propertyData.precio_dolares)}` 
                : propertyData.precio_soles > 0 
                  ? `S/ ${formatPrice(propertyData.precio_soles)}` 
                  : 'Consultar'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LuxuryHomeFlyer;